import { useIsFetching, useIsMutating } from '@zg-rentals/react-query5';
import { useHistory } from 'react-router';

import { Anchor, Avatar, Box, Button, Divider, Heading, List, MediaObject, Paragraph } from '@zillow/constellation-10';
import { LoadingButton, LoadingText } from '@zg-rentals/loading-components';

import { ZILLOW_TERMS_OF_USE_LINKS } from '../../constants/stringConstants';
import { useInfoForUser } from '../../utils/queries';
import NoAccessLeaseRenewalAlert from '../../components/Widgets/NoAccessLeaseRenewalAlert';
import IdentityContentBox from '../../components/Widgets/IdentityContentBox';
import LeaseExtension from '../../models/LeaseExtension';
import ColandlordFaq from '../../components/Containers/ColandlordContentContainer/ColandlordFaq';
import ParticipantContainer from '../../components/Containers/ParticipantContainer';
import { useLeasingUrlParams } from '../../hooks';

export function ColandlordWelcome() {
  const queryParameters = new URLSearchParams(window.location.search);
  const ouid = queryParameters.get('ouid');
  const infoForUserQuery = useInfoForUser(ouid, !!ouid);
  const { leaseId, renewalId } = useLeasingUrlParams();

  const isFetching = useIsFetching() > 0;
  const isMutating = useIsMutating() > 0;
  const history = useHistory();

  function onViewLeaseRenewalSummary() {
    history.push(LeaseExtension.getSummaryPath(leaseId, renewalId));
  }

  const sharedBodyContent = (
    <>
      <Heading
        level={2}
        css={{
          textStyle:
            'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
          marginBottom: 'tight',
        }}
      >
        Your lease renewal agreement is ready to sign
      </Heading>
      <Paragraph css={{ marginBottom: 'tighter' }}>
        {infoForUserQuery?.data?.firstName ? `Hi ${infoForUserQuery.data.firstName},` : 'Hi,'}
      </Paragraph>
      <Paragraph css={{ marginBottom: 'tight' }}>
        We’re so glad you’re here. Together with your co-landlord, we’ll help you review and sign your lease renewal
        agreement.
      </Paragraph>
      <Box css={{ backgroundColor: 'bg.softer', padding: 'loose', borderRadius: 'tight' }}>
        <Heading
          level={3}
          css={{
            textStyle:
              'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
          }}
        >
          How online lease renewals work
        </Heading>
        <List.Root>
          <List.Item>
            Review the lease renewal agreement, and ask your co-landlord to change anything that doesn’t seem right
          </List.Item>
          <List.Item>
            Your co-landlord revises the lease renewal agreement and arranges for your tenants to sign it online
          </List.Item>
          <List.Item>
            Once your tenants sign the lease renewal agreement, we’ll let you know that it’s ready for you to
            countersign
          </List.Item>
          <List.Item>The lease renewal agreeement is ratified when you and your co-landlord countersign</List.Item>
        </List.Root>
      </Box>
    </>
  );

  const termsText = (
    <Paragraph css={{ textStyle: 'body-sm' }}>
      By <strong>continuing to view your lease renewal</strong>, you agree to comply with the{' '}
      <Anchor target="_blank" rel="noopener noreferrer" href={ZILLOW_TERMS_OF_USE_LINKS.TERMS_OF_USE}>
        Terms of Use
      </Anchor>{' '}
      and the{' '}
      <Anchor target="_blank" rel="noopener noreferrer" href={ZILLOW_TERMS_OF_USE_LINKS.RENTER_TERMS_OF_USE}>
        Rental User Terms
      </Anchor>
      .
    </Paragraph>
  );

  return (
    <ParticipantContainer rightSidebarContent={<ColandlordFaq isLeaseRenewal />}>
      <>
        {!ouid ? (
          <>
            <NoAccessLeaseRenewalAlert />
            <Box css={{ marginBottom: 'tight' }} />
            {sharedBodyContent}
            <Button tone="brand" emphasis="filled" fluid disabled css={{ marginBottom: 'tighter', marginTop: 'tight' }}>
              View lease renewal
            </Button>
            {termsText}
          </>
        ) : (
          <>
            {infoForUserQuery.isError && (
              <>
                <NoAccessLeaseRenewalAlert />
                <Box css={{ marginBottom: 'tight' }} />
              </>
            )}
            {sharedBodyContent}
            <Box css={{ marginTop: 'tight' }} />
            {infoForUserQuery.isError || infoForUserQuery.isFetching ? (
              <>
                <MediaObject css={{ alignItems: 'center' }} media={<Avatar size="lg" />}>
                  <LoadingText isLoading loadingWidth="60%" css={{ marginBottom: 'tighter' }}>
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="80%">
                    Loading
                  </LoadingText>
                </MediaObject>
                <Divider css={{ marginTop: 'sm', marginBottom: 'sm' }} />
                <LoadingButton isLoading css={{ marginBottom: 'tighter' }}>
                  View lease renewal
                </LoadingButton>
                {termsText}
              </>
            ) : (
              <IdentityContentBox
                firstName={infoForUserQuery?.data?.firstName}
                lastName={infoForUserQuery?.data?.lastName}
                email={infoForUserQuery?.data?.email}
              >
                <Button
                  tone="brand"
                  emphasis="filled"
                  fluid
                  disabled={infoForUserQuery.isError || isFetching || isMutating}
                  onClick={onViewLeaseRenewalSummary}
                  css={{ marginBottom: 'tighter' }}
                >
                  View lease renewal
                </Button>
                {termsText}
              </IdentityContentBox>
            )}
          </>
        )}
      </>
    </ParticipantContainer>
  );
}
